import React, { useState } from 'react';
import { Button, Tag } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { TextBodyRegular } from 'lib.typography';
import Avatar from 'lib.components/src/components/Avatar';
import 'lib.assets/src/match-motion.scss';
import type { PartnerMatchItem } from 'lib.store/src/type-attendee/match/models';
import { MatchStatuses } from 'lib.shared/src/constants';
import {
  MatchReasoningDetails,
  hasReasoningDetails,
  stripEmphasis,
} from '../../../../components/MatchReasoning';

/** The partner's avatar: its logo when we have one, the initials otherwise */
export const PartnerInitial: React.FC<{ name: string | null; logo?: string | null }> = ({
  name,
  logo,
}) => <Avatar url={logo || ''} name={name || '?'} size="32" shape="square" bordered />;

/**
 * The prototype opens the argument with a bold verdict sentence — split the
 * summary's first sentence off so the card can emphasize it. Markdown
 * emphasis is stripped first: it would break the sentence split too.
 */
const splitLeadSentence = (raw: string): [string, string] => {
  const text = stripEmphasis(raw);
  const idx = text.indexOf('. ');
  return idx === -1 ? [text, ''] : [text.slice(0, idx + 1), text.slice(idx + 2)];
};

/**
 * The full match card as dealt into the feed — direction B of the prototype
 * (chosen 29 Oct): structured text with a label column, so the eye can jump —
 * why for you, what they do, what you would learn; no pictures. Then the
 * three decisions of the flow: Skip, Ask about, Shortlist.
 */
export const DeckCard: React.FC<{
  match: PartnerMatchItem;
  onSkip: () => void;
  onShortlist: () => void;
  onAsk: () => void;
  isActing: boolean;
}> = ({ match, onSkip, onShortlist, onAsk, isActing }) => {
  const like = (match.status.actions ?? []).find((a) => a.slug === MatchStatuses.liked);
  const pass = (match.status.actions ?? []).find((a) => a.slug === MatchStatuses.passed);

  // the verdict is the summary's first sentence — the card shows it alone
  const [lead] = splitLeadSentence(match.reasoning?.summary ?? '');
  // clamp at render — an overshooting payload is trimmed, never rejected
  const learnings = (match.reasoning?.meeting_learnings ?? []).slice(0, 3);

  return (
    // the arrival: a dealt card descends into the feed (bb v3 §05)
    <div className="match-deck-card mx-arrive">
      <div className="match-deck-card__header">
        <PartnerInitial name={match.partner.name} logo={match.partner.logo} />
        <h3 className="match-deck-card__name">{match.partner.name}</h3>
        {match.is_origin && <Tag color="purple">Your connection</Tag>}
        {match.site && (
          <a
            className="match-deck-card__site"
            href={`https://${match.site}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {match.site} ↗
          </a>
        )}
      </div>

      <dl className="match-deck-card__facts">
        {lead && (
          <>
            <dt>Why for you</dt>
            <dd>
              <b>{lead}</b>
            </dd>
          </>
        )}
        {match.what_they_do && (
          <>
            <dt>What they do</dt>
            <dd>{match.what_they_do}</dd>
          </>
        )}
        {!!learnings.length && (
          <>
            <dt>You would learn</dt>
            <dd>
              <ul className="match-deck-card__learnings">
                {learnings.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </dd>
          </>
        )}
      </dl>

      {match.partner.unclaimed && (
        <div className="match-deck-card__avail">Not on Match Express yet</div>
      )}

      <div className="match-deck-card__actions">
        {pass && (
          <Button disabled={isActing} onClick={onSkip} data-test-id="match-skip">
            Skip
          </Button>
        )}
        <Button
          type="text"
          className="match-deck-card__ask"
          onClick={onAsk}
          data-test-id="match-chat"
        >
          Ask about {match.partner.name}
        </Button>
        <span className="match-deck-card__spacer" />
        {like && (
          <Button
            type="primary"
            className="match-btn-period"
            loading={isActing}
            onClick={onShortlist}
            data-test-id="match-shortlist"
          >
            Shortlist
          </Button>
        )}
      </div>
    </div>
  );
};

/**
 * A decided card, collapsed into one line so the feed does not run forever —
 * the decision stays visible, the argument reopens on demand.
 */
export const CollapsedCard: React.FC<{
  match: PartnerMatchItem;
  how: 'kept' | 'skipped';
}> = ({ match, how }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="match-deck-card match-deck-card--collapsed">
      <div className="match-deck-card__collapsed-row">
        <PartnerInitial name={match.partner.name} logo={match.partner.logo} />
        <div className="match-deck-card__collapsed-title">
          <TextBodyRegular className="text-color-neutral-grey-10 match-deck-card__collapsed-name">
            {match.partner.name}
          </TextBodyRegular>
          {match.event && (
            <TextBodyRegular className="text-color-neutral-grey-8">
              {match.event.name}
            </TextBodyRegular>
          )}
        </div>
        <span
          className={`match-deck-card__verdict match-deck-card__verdict--${how}`}
          data-test-id="match-collapsed-verdict"
        >
          {how === 'kept' ? 'Shortlisted' : 'Skipped'}
        </span>
        {/* the same chevron the Shortlisted rows expand with — a labelled
            button pushed the verdict off the line on a phone */}
        <Button
          className="match-deck-card__collapsed-toggle"
          type="text"
          size="small"
          icon={<RightOutlined rotate={open ? 90 : 0} />}
          aria-expanded={open}
          aria-label={open ? 'Hide details' : 'Show details'}
          title={open ? 'Hide details' : 'Show details'}
          onClick={() => setOpen((v) => !v)}
        />
      </div>
      {open && (
        <div className="match-deck-card__collapsed-body">
          {match.reasoning?.summary && (
            <TextBodyRegular className="match-deck-card__summary">
              {stripEmphasis(match.reasoning.summary)}
            </TextBodyRegular>
          )}
          {hasReasoningDetails(match) && <MatchReasoningDetails match={match} />}
        </div>
      )}
    </div>
  );
};
